import { useMemo, useCallback, useState } from "react";
import { Box, Button, Tooltip } from "@material-ui/core";
import ViewModuleOutlinedIcon from "@material-ui/icons/ViewModuleOutlined";
import DataListHeader from "components/DataListHeader";
import DataTable from "components/DataTable/DataTable";
import LoadingSpinner from "components/LoadingSpinner";
import PageComponent from "components/Page/PageComponent";
import { useGetClientsQuery } from "features/clients/clientSlice";
import { useGetClientsWithConfigsQuery } from "features/clients/clientConfigSlice";
import { useGetLotsQuery } from "features/lots/lotSlice";
import { getClientScopedLots } from "features/lots/lotUtil";
import { ALL_TENANT_LOTS_OPTION_ID } from "components/SharedDropdowns/LotsDropDown/LotsDropDown";
import { useGetPricingByClientQuery } from "features/pricing/pricingSlice";
import { useGetVendorsQuery } from "features/vendor/vendorSlice";
import { arrayToObject } from "utils/object-util";
import { fieldTypes } from "services/filterService";
import useSortOptions from "hooks/useSortOptions";
import { PricingByClientStyle } from "./PricingByClientStyle";
import { IconEdit, IconSearch } from "icons";
import { useGetAllActivityItemsQuery } from "features/activity/activitySlice";
import PricingLotDetailsDialog from "./Components/PricingLotDetailsDialog/PricingLotDetailsDialog";
import {
  EMPTY_STATE_MESSAGE,
  NO_ITEMS_MESSAGE,
} from "./PricingByClientConst";
import {
  buildPricingMatrixData,
  getPricingCellStyle,
} from "./pricingByClientUtils";
import PricingLotCell from "./Components/PricingLotCell";
import PricingLegend from "./Components/PricingLegend";
import { Alert } from "@mui/material";
import { useGetAllBundlesQuery } from "features/activityBundle/activityBundleSlice";
import ActivityPricingManagementDialog, { ACTIVITY_PRICING_DIALOG_MODES } from "../CreateActivityAndPricing/CreateActivityAndPricingDialog";
import BundleDialog, { BUNDLE_DIALOG_MODES } from "../CreateBundle/BundleDialog";
import { CREATE_ACTIVITY_PRICING_STEP_INDEX } from "../CreateActivityAndPricing/createActivityAndPricingConsts";
import { CREATE_BUNDLE_STEP_INDEX } from "../CreateBundle/createBundleConsts";
import { LOT_OPTIONS } from "../CreateActivityAndPricing/ActivityItemPricing/ActivityItemPricingConsts";
import { v4 as uuidv4 } from "uuid";

const PricingByClient = () => {
  const classes = PricingByClientStyle();
  const [sortOptions, sortData] = useSortOptions();

  const [selectedClientId, setSelectedClientId] = useState(null);
  const [selectedLotIds, setSelectedLotIds] = useState([ALL_TENANT_LOTS_OPTION_ID]);
  const [searchParams, setSearchParams] = useState(null);
  const [selectedPricingCell, setSelectedPricingCell] = useState(null);
  const [initialDialogTab, setInitialDialogTab] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [activityItemIdToEdit, setActivityItemIdToEdit] = useState(null);
  const [pricingPrefill, setPricingPrefill] = useState(null);

  // Edit bundle state
  const [openBundleEditDialog, setOpenBundleEditDialog] = useState(false);
  const [bundleIdToEdit, setBundleIdToEdit] = useState(null);
  const [bundlePrefill, setBundlePrefill] = useState(null);

  const onDropFilter = (value, prop) => {
    if (prop === "clientId") {
      setSelectedClientId(value);
      setSelectedLotIds([ALL_TENANT_LOTS_OPTION_ID]);
      setSearchParams(null);
    } else if (prop === "lotIds") {
      setSelectedLotIds(value || []);
    }
  };

  let { data: clients, isLoading: isLoadingClients } = useGetClientsQuery(null);
  let { data: lots, isLoading: isLoadingLots } = useGetLotsQuery();
  let { data: clientsWithConfigs } = useGetClientsWithConfigsQuery(null);
  let { data: activityPricing, isLoading: isInitialLoadingActivityPricing, isFetching: isFetchingActivityPricing } = useGetPricingByClientQuery(searchParams, { skip: !searchParams });
  let { data: vendors, isLoading: isLoadingVendors } = useGetVendorsQuery();
  let { data: activityItems, isLoading: isLoadingActivityItems } = useGetAllActivityItemsQuery();
  let { data: bundles, error: bundlesError, isFetching: isLoadingBundles } = useGetAllBundlesQuery();

  const vendorLookup = arrayToObject(vendors);
  const activityItemLookup = arrayToObject(activityItems);
  const lotsLookup = arrayToObject(lots);
  const bundlesLookup = arrayToObject(bundles);

  // "All Tenant Lots" is stored as a single sentinel so the filter shows one chip.
  // Resolve it here to the tenant-owned lot ids within the current client-scoped list.
  const effectiveLotIds = useMemo(() => {
    if (!selectedLotIds?.includes(ALL_TENANT_LOTS_OPTION_ID)) {
      return selectedLotIds;
    }
    return getClientScopedLots(lots, clientsWithConfigs, selectedClientId)
      .filter((lot) => lot.ownedByTenant)
      .map((lot) => lot.id);
  }, [selectedLotIds, lots, clientsWithConfigs, selectedClientId]);

  const handleSearch = () => {
    setSearchParams(selectedClientId);
  };

  const handlePricingCellClick = (pricingData, row, lotId) => {
    if (!pricingData) {
      // State 3: no pricing for this client/lot — open the edit dialog directly on the
      // Pricing step with a new pricing item pre-seeded for this client + specific lot.
      // The user only needs to pick vendor(s), preferred vendor, and cost.
      if (row?.activityItemId) {
        setActivityItemIdToEdit(Number(row.activityItemId));
        setPricingPrefill({
          startStep: CREATE_ACTIVITY_PRICING_STEP_INDEX.PRICING,
          newPricingItem: {
            tempId: `${Date.now()}-${uuidv4()}`,
            isNew: true,
            clientId: Number(selectedClientId),
            lotOption: LOT_OPTIONS.SPECIFIC_LOTS,
            selectedLotIds: [lotId],
          },
        });
        setOpenDialog(true);
      } else if (row?.bundleId) {
        // Bundles are edited through the bundle dialog. Open it directly on the
        // Pricing step with a new pricing item pre-seeded for this client + lot.
        setBundlePrefill({
          startStep: CREATE_BUNDLE_STEP_INDEX.PRICING,
          newPricingItem: {
            tempId: `${Date.now()}-${uuidv4()}`,
            isNew: true,
            clientId: Number(selectedClientId),
            lotOption: LOT_OPTIONS.SPECIFIC_LOTS,
            selectedLotIds: [lotId],
          },
        });
        setBundleIdToEdit(Number(row.bundleId));
        setOpenBundleEditDialog(true);
      }
      return;
    }

    if (!row?.bundleId && pricingData.hasPricingCombinations && !pricingData.vendorId) {
      // State 2: pricing combinations exist but no preferred vendor selected.
      // Bundles have no preferred-vendor tab, so this never applies to them.
      setInitialDialogTab(1);
    } else {
      // State 1: preferred vendor configured (or a bundle cell).
      setInitialDialogTab(0);
    }
    setSelectedPricingCell(pricingData);
  };

  const handlePricingDialogClose = () => {
    setSelectedPricingCell(null);
    setInitialDialogTab(0);
  };

  const onEditPricingRow = useCallback((row) => {
    if (!row?.activityItemId) {
      return;
    }

    setActivityItemIdToEdit(Number(row.activityItemId));
    setPricingPrefill(null);
    setOpenDialog(true);
  }, []);

  const handleCloseEditDialog = () => {
    setOpenDialog(false);
    setActivityItemIdToEdit(null);
    setPricingPrefill(null);
  };

  const onEditBundle = useCallback((row) => {
    if (!row?.bundleId) {
      return;
    }

    setBundlePrefill(null);
    setBundleIdToEdit(Number(row.bundleId));
    setOpenBundleEditDialog(true);
  }, []);

  const handleBundleEditClose = () => {
    setOpenBundleEditDialog(false);
    setBundleIdToEdit(null);
    setBundlePrefill(null);
  };


  const filterConfig = [
    { type: fieldTypes.TEXT, size: 2, title: "Client", key: "clientId", values: clients },
    {
      type: fieldTypes.LOT,
      size: 5,
      title: "Lots",
      key: "lotIds",
      values: lots,
      multiple: true,
      allTenantLotsOption: true,
    },
    {
      type: fieldTypes.COMPONENT,
      size: 1,
      key: "searchButton",
      component: (
        <Box display="flex" alignItems="flex-end">
          <Button
            color="primary"
            variant="contained"
            onClick={handleSearch}
            disabled={!selectedClientId}
            startIcon={<IconSearch fill="white" />}
            style={{ height: "40px", marginTop: "7px" }}
          >
            Search
          </Button>
        </Box>
      ),
    },
  ];

  let filters = {
    ...(selectedClientId && { clientId: { value: selectedClientId } }),
    lotIds: { value: selectedLotIds },
  };

  // Reactively filter API data by selected lots and build matrix rows.
  const { rows: matrixData, lotIdsFromData } = useMemo(() => {
    return buildPricingMatrixData({
      activityPricing,
      selectedLotIds: effectiveLotIds,
      activityItemLookup,
      vendorLookup,
      bundlesLookup,
    });
  }, [activityPricing, effectiveLotIds, activityItemLookup, vendorLookup, bundlesLookup]);

  // If lots are selected in filter, show all selected lots as columns (even if empty).
  // Otherwise, derive lot columns from lots that have data in current result.
  const lotsForColumns = useMemo(() => {
    const lotIdsForColumns = effectiveLotIds?.length > 0
      ? Array.from(new Set(effectiveLotIds))
      : Array.from(lotIdsFromData);

    return lotIdsForColumns
      .map((id) => lotsLookup[id])
      .filter(Boolean)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [effectiveLotIds, lotIdsFromData, lotsLookup]);

  // Configure fixed activity column + one column per lot that has data.
  const columns = useMemo(() => {
    return [
      {
        name: "Activity",
        sortKey: "activityName",
        key: "activityName",
        width: 200,
        component: (row) => (
          <div className={classes.activityRow}>
            <div className={classes.activityCell}>{row?.activityName}</div>
            {!!row?.bundleId && (
              <Tooltip title="Bundle" arrow>
                <ViewModuleOutlinedIcon className={classes.activityIcon} />
              </Tooltip>
            )}
          </div>
        ),
      },
      {
        name: "Actions",
        key: "editActivity",
        width: 100,
        component: (row) => {
          const isBundleRow = !!row?.bundleId;

          return (
            <Button
              variant="outlined"
              color="primary"
              size="small"
              onClick={() => isBundleRow ? onEditBundle(row) : onEditPricingRow(row)}
              startIcon={<IconEdit />}
            >
              Edit
            </Button>
          );
        },
      },
      ...lotsForColumns.map((lot) => ({
        name: lot.name,
        sortKey: `lot_${lot.id}`,
        key: `lot_${lot.id}`,
        width: 120,
        cellStyle: (row) =>
          getPricingCellStyle({
            row,
            lotId: lot.id,
          }),
        component: (row) => {
          const pricingData = row[`lot_${lot.id}`];
          return (
            <PricingLotCell
              pricingData={pricingData}
              classes={classes}
              isBundle={!!row?.bundleId}
              onClick={() => handlePricingCellClick(pricingData, row, lot.id)}
            />
          );
        },
      })),
    ];
  }, [lotsForColumns, classes, onEditPricingRow, onEditBundle]);

  const mapExportData = useCallback(
    (row) => {
      const exportRow = {
        Activity: row.activityName,
      };

      lotsForColumns.forEach((lot) => {
        const pricingData = row[`lot_${lot.id}`];
        exportRow[lot.name] = pricingData ? pricingData.formattedCharge : "-";
      });

      return exportRow;
    },
    [lotsForColumns]
  );

  // First load (no data yet) replaces the table with a spinner. A background refetch
  // (e.g. after a save invalidates the cache) keeps the table mounted — RTK Query retains
  // the previous data for the same args — and only shows the overlay spinner while it updates.
  const isInitialLoading =
    isLoadingClients ||
    isLoadingLots ||
    isInitialLoadingActivityPricing ||
    isLoadingVendors ||
    isLoadingActivityItems;

  const isRefreshing = isFetchingActivityPricing;

  const showTable = searchParams !== null;

  return (
    <PageComponent
      header={
        <DataListHeader
          data={matrixData}
          dropDownsConfig={filterConfig}
          filters={filters}
          onDropFilter={onDropFilter}
        />
      }
    >
      {!showTable ? (
        <div className={classes.emptyStateMessage}>{EMPTY_STATE_MESSAGE}</div>
      ) : (
        <>
          <LoadingSpinner loading={isInitialLoading || isRefreshing} />
          <PricingLegend />
          {!isInitialLoading &&
            <>
              {!selectedLotIds?.length &&
                <Alert severity="warning" className={classes.alert}>
                  {"Note: For better performance, consider filtering by specific lots if you have a large dataset."}
                </Alert>}
              <DataTable
                columns={columns}
                rows={matrixData}
                rowIdentifier="id"
                onSort={sortData}
                mapExportData={mapExportData}
                sortDirection={sortOptions.sortDirection}
                columnToSort={sortOptions.columnToSort}
                noItemsMessage={NO_ITEMS_MESSAGE}
                headerStyle={{
                  paddingLeft: "1rem",
                }}
                isLoading={isRefreshing}
              />
            </>}
        </>
      )}

      <PricingLotDetailsDialog
        open={!!selectedPricingCell}
        onClose={handlePricingDialogClose}
        pricingData={selectedPricingCell}
        initialTab={initialDialogTab}
      />

      <ActivityPricingManagementDialog
        open={openDialog}
        activityItemId={activityItemIdToEdit}
        handleClose={handleCloseEditDialog}
        mode={ACTIVITY_PRICING_DIALOG_MODES.EDIT}
        prefill={pricingPrefill}
      />

      <BundleDialog
        open={openBundleEditDialog}
        bundleId={bundleIdToEdit}
        handleClose={handleBundleEditClose}
        mode={BUNDLE_DIALOG_MODES.EDIT}
        prefill={bundlePrefill}
      />
    </PageComponent>
  );
};

export default PricingByClient;
